import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { toastStore } from 'shared/ui'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { type AccessRequest } from 'entities/Users/model/AccessRequest'
import { UserAccessRequestType } from 'entities/Users'
import { UsersApi } from 'entities/Users/api/users'

interface IRejectTexts {
  modalTitle: string
  modalDesc: (fullName: string, email: string) => string
  successToast: string
}

const rejectTextsMap: Record<UserAccessRequestType, IRejectTexts> = {
  [UserAccessRequestType.SalesmsgAccess]: {
    modalTitle: 'Reject Salesmsg access?',
    modalDesc: (fullName, email) => `${fullName} (${email}) won't be able to access to Salesmsg`,
    successToast: 'Salesmsg access rejected',
  },
  [UserAccessRequestType.InboxAccess]: {
    modalTitle: 'Reject inbox request?',
    modalDesc: (fullName, email) =>
      `${fullName} (${email}) won't be able to access requested inbox`,
    successToast: 'Inbox access rejected',
  },
  [UserAccessRequestType.PermissionsUpdate]: {
    modalTitle: 'Reject permissions update?',
    modalDesc: (fullName, email) =>
      `${fullName} (${email}) won't be able to access the requested permissions`,
    successToast: 'Permissions rejected',
  },
}

export class RejectAccessRequestStore {
  private readonly _modalId = nanoid()
  private _accessRequest: AccessRequest | null = null

  constructor(private _onSuccess?: () => void) {
    makeAutoObservable(this)
  }

  openModal = (accessRequest: AccessRequest) => {
    this._accessRequest = accessRequest

    const texts = rejectTextsMap[accessRequest.requestType]

    const { fullName, email } = accessRequest.user
    const desc = texts.modalDesc(fullName, email)

    modalStore.addModal({
      id: this._modalId,
      type: ModalTypeList.INFO,
      title: texts.modalTitle,
      desc,
      primaryAction: {
        text: 'Reject',
        onAction: this.onReject,
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: this.closeModal,
      },
    })
  }

  closeModal = () => {
    modalStore.removeModal(this._modalId)
  }

  onReject = async () => {
    if (!this._accessRequest) return

    const texts = rejectTextsMap[this._accessRequest.requestType]

    try {
      await UsersApi.rejectAccessRequest(this._accessRequest.id)

      this.closeModal()

      toastStore.add({
        type: 'success',
        title: texts.successToast,
      })

      this._onSuccess?.()
    } catch (error) {
      toastStore.add({
        type: 'error',
        title: 'Failed to reject request',
      })
    }
  }
}
